import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, useWindowDimensions } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { ds } from '@/components/lib/design.system';
import { useCurrencyOverviewModal } from '@/feature/account-details-sweeps/components/CurrencyOverviewModalProvider';
import { DepositButton } from '@/feature/account-details/components/DepositButton';
import { useWithdrawalStatus } from '@/feature/account-details/hooks/use-withdrawal-status';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useShowTncSheet } from '@/feature/terms-and-conditions/hooks/use-show-tnc-sheet';
import { useNoPaymentMethodDeposit } from '@/feature/withdraw/hooks/use-no-payment-method-deposit';
import { useStartWithdrawVerification } from '@/feature/withdraw/hooks/use-start-withdraw-verification';
import { useOpenWithdrawStack } from '@/feature/withdraw/hooks/use-withdraw-navigation';
import { useCurrencyGuidance } from '@/hooks/use-currency-guidance';
import { Product } from '@/hooks/use-jurisdiction';
import { SocialCurrency } from '@/hooks/use-social-currency';
import { common } from '@/styles/styles';

import { useNonWithdrawableVerificationCheck } from '../../hooks/use-non-withdrawable-verification-check';
import { WithdrawVerificationOutcome } from '../../utils/resolve-withdraw-verification-outcome';

const SocialWalletActionButtons = ({
    onWithdrawPress,
    isWithdrawLoading,
}: {
    onWithdrawPress: () => void;
    isWithdrawLoading: boolean;
}) => {
    const { t } = useTranslation(['wallet']);
    const navigation = useNavigation();
    const { showCurrencyOverviewModal } = useCurrencyOverviewModal();
    const { shouldShow, markAsShown } = useCurrencyGuidance();
    const { width: screenWidth } = useWindowDimensions();
    const showTncSheet = useShowTncSheet();

    const handleAddToWallet = () => {
        // Currency Guidance Modal -- only appear if never shown before
        if (shouldShow) {
            markAsShown();
            showCurrencyOverviewModal({
                isTouchpoint: false,
                currency: SocialCurrency.COINS,
            });
            return;
        }

        showTncSheet(() => navigation.navigate('DepositStack', { screen: 'CoinPackageScreen' }));
    };

    return (
        <>
            <Button label={t('wallet:add_to_wallet')} hierarchy="primary" onPress={handleAddToWallet} />
            <SizedBox value={12} />
            <Button
                label={t('wallet:redeem')}
                onPress={onWithdrawPress}
                loading={isWithdrawLoading}
                style={styles.withdrawRedeemButton}
                hierarchy={'subtle'}
            />
            <SizedBox value={24} />
            <LineSeparator
                style={{
                    width: screenWidth,
                    marginLeft: -common.paddingHorizontal.paddingHorizontal,
                }}
            />
            <SizedBox value={24} />
        </>
    );
};

type WalletActionButtonsProps = {
    product: Product;
};

export const WalletActionButtons = ({ product }: WalletActionButtonsProps) => {
    const { t } = useTranslation(['wallet', 'account', 'common']);
    const openWithdrawStack = useOpenWithdrawStack();
    const { canWithdraw, hasWithdrawMethods, isLoading, infoSheetParams } = useWithdrawalStatus();
    const { description: noPaymentMethodDescription, depositLabel, navigateToDeposit } = useNoPaymentMethodDeposit();
    const { showInfoSheet } = useAlerts();
    const isSharedSocialWallet = product === Product.Sweeps || product === Product.Casino;
    const { width: screenWidth } = useWindowDimensions();
    const { outcome, isVerificationCheckLoading } = useNonWithdrawableVerificationCheck();
    const startWithdrawVerification = useStartWithdrawVerification();
    const isWithdrawLoading = isLoading || isVerificationCheckLoading;

    const onWithdrawPress = useCallback(() => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.WALLET_WITHDRAW);

        // Order of this logic is important, we do not want to combine !canWithdraw and !hasWithdrawMethods
        if (!canWithdraw) {
            showInfoSheet(infoSheetParams);
            return;
        }

        if (outcome !== WithdrawVerificationOutcome.CLEARED) {
            startWithdrawVerification();
            return;
        }

        if (!hasWithdrawMethods) {
            BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAW_NO_PAYMENT_METHOD);
            showInfoSheet({
                title: t('account:withdraw_no_payment_method_title'),
                description: noPaymentMethodDescription,
                buttonLabel: depositLabel,
                handlePress: navigateToDeposit,
                secondaryLabel: t('common:close'),
            });
            return;
        }

        openWithdrawStack();
    }, [
        canWithdraw,
        openWithdrawStack,
        showInfoSheet,
        infoSheetParams,
        outcome,
        startWithdrawVerification,
        hasWithdrawMethods,
        t,
        noPaymentMethodDescription,
        depositLabel,
        navigateToDeposit,
    ]);

    if (isSharedSocialWallet) {
        return <SocialWalletActionButtons onWithdrawPress={onWithdrawPress} isWithdrawLoading={isWithdrawLoading} />;
    }

    return (
        <>
            <DepositButton customLabel={t('deposit')} />
            <SizedBox value={12} />
            <Button
                label={t('wallet:withdraw')}
                onPress={onWithdrawPress}
                loading={isWithdrawLoading}
                style={styles.withdrawRedeemButton}
                hierarchy={'subtle'}
            />
            <SizedBox value={24} />
            <LineSeparator
                style={{
                    width: screenWidth,
                    marginLeft: -common.paddingHorizontal.paddingHorizontal,
                }}
            />
            <SizedBox value={24} />
        </>
    );
};

const styles = StyleSheet.create({
    withdrawRedeemButton: {
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
    },
});
